import type { IAgoraRTCClient, ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { changeVideoInputDevice, initializeVideoInput, type VideoInput } from '../video/videoHelpers';
import {
  BACKGROUND_OPTIONS,
  getVFXConfig,
  teardownPipeline,
  transformVideo
} from '../virtualBackgrounds/virtualBackground';

import { StudioLogger } from '@studio/logger/StudioLogger';
import { EXCLUDED_VIRTUAL_DEVICES } from '@studio/constants/DeviceTypes';

const LOG = new StudioLogger('agoraDeviceListeners');

// Reference to the visibilitychange handler so it can be removed on cleanup
let visibilityChangeHandler: (() => void) | null = null;

/* eslint-disable no-unused-vars */
interface DispatchFunction {
  (action: any): void;
}

/* eslint-disable no-unused-vars */
interface Actions {
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  showNotification: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
}

/* eslint-disable no-unused-vars */
export interface VideoActions {
  showNotification: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
  setAttendeeCameraStatus: (...args: any[]) => any;
  setVideoInputDevice: (...args: any[]) => any;
}

/**
 * Handles camera device changes (connection/disconnection) and manages video input state accordingly.
 *
 * When a camera is disconnected and no other cameras are available, disables the attendee's camera.
 * When a camera is disconnected but alternatives exist, switches to the default camera.
 * When a new camera is connected, switches to the newly connected camera.
 *
 * @param currentVideoInput - The current video input object containing the video track
 * @param changedDevice - The device change event object from Agora containing device state and info
 * @param dispatch - Redux dispatch function to trigger state updates
 * @param actions - Object containing action creators for video-related state changes
 * @param attendeeId - The unique identifier for the attendee whose camera is being managed
 * @returns Promise<VideoInput> - The updated video input object after handling the device change
 *
 * @example
 * ```typescript
 * const updatedVideoInput = await handleCameraDeviceChange(
 *   currentVideoInput,
 *   { state: 'INACTIVE', device: { deviceId: 'cam1', label: 'Camera 1' } },
 *   dispatch,
 *   { setAttendeeCameraStatus, addToAttendeeTiles, setVideoInputDevice },
 *   'attendee-123'
 * );
 * ```
 */
async function handleCameraDeviceChange(
  currentVideoInput: VideoInput,
  changedDevice: any,
  dispatch: DispatchFunction,
  actions: VideoActions,
  attendeeId: string,
  client?: IAgoraRTCClient
): Promise<VideoInput> {
  if (!currentVideoInput.videoTrack) return currentVideoInput;

  // Check if camera is intentionally off (track not published)
  const isCameraOff = client && !client.localTracks?.includes(currentVideoInput.videoTrack);

  try {
    const changedDeviceLabel = changedDevice.device.label;
    const isDisconnectedDeviceCurrent = currentVideoInput.device === changedDevice.device.deviceId;
    const currentDeviceLabel = currentVideoInput.videoTrack.getTrackLabel();

    LOG.info(`Camera device change detected: ${changedDeviceLabel} is now ${changedDevice.state}`, {
      changedDevice,
      isDisconnectedDeviceCurrent
    });
    const isDeviceDisconnected = isDisconnectedDeviceCurrent && changedDevice.state === 'INACTIVE';
    const isDeviceConnected = changedDevice.state === 'ACTIVE';
    const devices = await AgoraRTC.getCameras();
    const defaultVideoDevice = devices[0]?.deviceId;
    const defaultVideoLabel = devices[0]?.label;
    const newDeviceId = changedDevice.device.deviceId;
    // if the current device is unplugged and there are no other devices available, we need to toggle the camera off since there is no longer an active device

    if (isDeviceDisconnected && devices.length === 0) {
      LOG.info(`Camera ${changedDeviceLabel} disconnected. No other cameras available, toggling camera off.`);
      dispatch(actions.setAttendeeCameraStatus(attendeeId, false));
      const _videoInput: VideoInput = {
        ...currentVideoInput,
        device: ''
      };
      dispatch(actions.setVideoInputDevice(_videoInput));
      return _videoInput;
    }

    // if the current device is inactive but there are other devices available, we can attempt to switch to the default device
    if (isDeviceDisconnected && defaultVideoDevice) {
      LOG.info(`Camera ${changedDeviceLabel} disconnected. Changing to default device - ${defaultVideoLabel}.`);
      const _videoInput = await changeVideoInputDevice(currentVideoInput, defaultVideoDevice, client);
      dispatch(actions.addToAttendeeTiles(attendeeId, _videoInput.videoTrack));
      dispatch(actions.setVideoInputDevice(_videoInput));

      if (isCameraOff) {
        // Keep camera off but with a valid track ready for the default device
        if (_videoInput.videoTrack) {
          await _videoInput.videoTrack.setEnabled(false);
          if (client?.localTracks?.includes(_videoInput.videoTrack)) {
            await client.unpublish(_videoInput.videoTrack);
          }
        }
      } else {
        dispatch(actions.setAttendeeCameraStatus(attendeeId, true));
        dispatch(
          actions.showNotification(
            `Camera ${changedDeviceLabel} disconnected. Switching from ${changedDeviceLabel} to ${defaultVideoLabel}`,
            {
              timeout: 5000,
              type: 'alert'
            }
          )
        );
      }
      return _videoInput;
    }

    // if a new device is connected, attempt to switch to it
    // Skip if we already switched to this device (e.g. INACTIVE event for USB camera
    // already triggered a fallback to the built-in camera, then ACTIVE event fires
    // for that same built-in camera)
    // When camera is off, ignore new device connections — don't turn camera on
    if (isCameraOff) return currentVideoInput;

    if (newDeviceId && devices.length > 0 && isDeviceConnected && currentVideoInput.device !== newDeviceId) {
      const _videoInput = await changeVideoInputDevice(currentVideoInput, newDeviceId, client);
      dispatch(actions.addToAttendeeTiles(attendeeId, _videoInput.videoTrack));
      dispatch(actions.setAttendeeCameraStatus(attendeeId, true));
      dispatch(actions.setVideoInputDevice(_videoInput));
      dispatch(
        actions.showNotification(`New Camera detected. Switching from ${currentDeviceLabel} to ${changedDeviceLabel}`, {
          timeout: 5000,
          type: 'alert'
        })
      );
      return _videoInput;
    }

    return currentVideoInput;
  } catch (error) {
    LOG.error('Error handling camera device change:', error);
    return currentVideoInput;
  }
}

function setupVideoDeviceListeners(
  videoInput: VideoInput,
  dispatch: DispatchFunction,
  actions: VideoActions,
  attendeeId: string,
  client?: IAgoraRTCClient
) {
  LOG.info('Setting up video device listeners for camera changes');
  let currentVideoInput = videoInput;
  // Serialize camera-changed events to prevent concurrent pipeline operations.
  // On some systems (e.g. macOS), unplugging a USB camera fires both an INACTIVE
  // event for the disconnected device and an ACTIVE event for the built-in camera
  // in rapid succession. Without serialization, both handlers read the same stale
  // currentVideoInput and race over the shared processor/pipedTrack singletons,
  // leaving processorDestination in a corrupted "already piped" state.
  let cameraChangeQueue: Promise<void> = Promise.resolve();

  AgoraRTC.on('camera-changed', (changedDevice: any) => {
    cameraChangeQueue = cameraChangeQueue.then(async () => {
      currentVideoInput = await handleCameraDeviceChange(
        currentVideoInput,
        changedDevice,
        dispatch,
        actions,
        attendeeId,
        client
      );
    });
  });

  // When the laptop lid closes the OS may release the camera, leaving the
  // MediaStreamTrack in an "ended" state and the Agora CameraVideoTrack
  // internally corrupted (e.g. _enabledMutex destroyed). Detect this on
  // visibility resume and proactively recreate the track so the user never
  // sees a black screen.
  const handleVisibilityChange = async () => {
    if (document.visibilityState !== 'visible') return;
    if (!currentVideoInput.videoTrack) return;

    // Only attempt recovery if the video track was actively published (camera was on).
    // If the user intentionally turned off their camera, the track won't be in localTracks.
    if (client && !client.localTracks?.includes(currentVideoInput.videoTrack)) return;

    const mediaStreamTrack = currentVideoInput.videoTrack.getMediaStreamTrack();
    if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') return;

    LOG.info('Page resumed with dead video track, recreating camera track', {
      device: currentVideoInput.device
    });

    try {
      const deviceId = currentVideoInput.device;
      if (!deviceId) return;

      // Wait for the client to reconnect before attempting publish/unpublish.
      // After lid reopen the WebSocket/WebRTC connection may still be re-establishing.
      if (client && client.connectionState !== 'CONNECTED') {
        LOG.info(`Client not connected (state: ${client.connectionState}), waiting for reconnect`);
        await new Promise<void>(resolve => {
          const onStateChange = (newState: string) => {
            if (newState === 'CONNECTED') {
              client.off('connection-state-change', onStateChange);
              resolve();
            }
          };
          client.on('connection-state-change', onStateChange);
        });
      }

      // Teardown any existing VB pipeline on the dead track
      teardownPipeline(currentVideoInput.videoTrack);

      // Unpublish and close old track — wrapped separately so a corrupted track
      // doesn't prevent us from publishing the new one
      if (client) {
        try {
          const localVideoTracks = client.localTracks?.filter(t => t.trackMediaType === 'video') || [];
          for (const track of localVideoTracks) {
            await client.unpublish(track);
            (track as ICameraVideoTrack).close();
          }
        } catch (unpublishError) {
          LOG.warn('Failed to unpublish dead track (may already be cleaned up by Agora):', unpublishError);
        }
      } else {
        try {
          currentVideoInput.videoTrack.close();
        } catch {
          // track already dead, ignore
        }
      }

      // Create fresh track
      const newVideoInput = await initializeVideoInput(deviceId);

      // Reapply virtual background if one was active
      if (currentVideoInput.virtualBackground !== BACKGROUND_OPTIONS.NONE && newVideoInput.videoTrack) {
        const vfxConfig = await getVFXConfig(
          currentVideoInput.virtualBackground,
          currentVideoInput.virtualBackgroundImageUrl
        );
        if (vfxConfig) {
          await transformVideo(newVideoInput.videoTrack, vfxConfig, deviceId);
        }
      }

      // Publish new track to remote participants
      if (newVideoInput.videoTrack && client) {
        await client.publish(newVideoInput.videoTrack);
        LOG.info('Published recreated video track to remote participants');
      }

      const restoredVideoInput: VideoInput = {
        ...newVideoInput,
        virtualBackground: currentVideoInput.virtualBackground,
        virtualBackgroundImageUrl: currentVideoInput.virtualBackgroundImageUrl,
        mirrored: currentVideoInput.mirrored
      };

      currentVideoInput = restoredVideoInput;
      dispatch(actions.addToAttendeeTiles(attendeeId, restoredVideoInput.videoTrack));
      dispatch(actions.setVideoInputDevice(restoredVideoInput));

      LOG.info('Successfully recreated video track after lid reopen');
    } catch (error) {
      LOG.error('Failed to recreate video track after lid reopen:', error);
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityChangeHandler = handleVisibilityChange;
}

function clearVideoDeviceListeners() {
  LOG.info('Clearing up video device listeners for camera changes');
  AgoraRTC.removeAllListeners('camera-changed');
  if (visibilityChangeHandler) {
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    visibilityChangeHandler = null;
  }
}

function setupAudioDeviceListeners(
  localAudioTrack: IMicrophoneAudioTrack | null,
  dispatch: DispatchFunction,
  actions: Actions
) {
  if (!localAudioTrack) {
    return;
  }

  AgoraRTC.on('microphone-changed', async (changedDevice: any) => {
    await handleDeviceChange(
      'audio',
      localAudioTrack,
      changedDevice,
      dispatch,
      actions.handleAudioInputChanged,
      actions.showNotification
    );
  });
}

function setupPlaybackDeviceListener() {
  AgoraRTC.on('playback-device-changed', (info: any) => {
    LOG.info(`Playback device changed: ${JSON.stringify(info)}`);
  });
}

async function handleDeviceChange(
  deviceType: 'audio' | 'video',
  deviceTrack: IMicrophoneAudioTrack | ICameraVideoTrack,
  changedDevice: any,
  dispatch: DispatchFunction,
  handleDevice: Function,
  showNotification: Function
) {
  let switchDevice = false;
  const deviceNaming = deviceType === 'audio' ? 'microphone' : 'camera';
  const currentDeviceLabel = deviceTrack.getTrackLabel();

  LOG.info(`Device Change Detected for ${deviceNaming} ${changedDevice.device.label}`, {
    changedDevice
  });

  // When a new device is detected, switch to the new device
  if (changedDevice.state === 'ACTIVE') {
    // Ignore virtual devices from third-party apps (e.g. Zoom, Teams)
    if (EXCLUDED_VIRTUAL_DEVICES.has(changedDevice.device.label)) {
      LOG.info(`Ignoring virtual device: ${changedDevice.device.label}`);
      return switchDevice;
    }

    try {
      await deviceTrack.setDevice(changedDevice.device.deviceId);
      dispatch(handleDevice(changedDevice.device.deviceId));
      switchDevice = true;
      LOG.info(`New ${deviceNaming} detected. Switching from ${currentDeviceLabel} to ${changedDevice.device.label}`);

      // show notification only if the device label is different aka switching to a brand new device
      if (currentDeviceLabel !== changedDevice.device.label) {
        dispatch(
          showNotification(
            `New ${deviceNaming} detected. Switching from ${currentDeviceLabel} to ${changedDevice.device.label}`,
            {
              timeout: 5000,
              type: 'alert'
            }
          )
        );
      }
    } catch (error) {
      LOG.error(`Error switching to new ${deviceNaming} device: ${error}`);
    }
  } else if (changedDevice.device.label === currentDeviceLabel) {
    let oldDevices;

    if (deviceType === 'audio') {
      oldDevices = await AgoraRTC.getMicrophones();
      oldDevices = oldDevices.filter(d => !EXCLUDED_VIRTUAL_DEVICES.has(d.label));
    } else {
      oldDevices = await AgoraRTC.getCameras();
    }

    // Switch to an existing device when the unplugged device is the current device
    if (oldDevices[0]) {
      await deviceTrack.setDevice(oldDevices[0].deviceId);
      dispatch(handleDevice(oldDevices[0].deviceId));
      switchDevice = true;

      LOG.info(
        `${deviceNaming} ${changedDevice.device.label} disconnected. Changing to default ${oldDevices[0].label}.`
      );
      dispatch(
        showNotification(`Your ${deviceNaming} ${changedDevice.device.label} disconnected. Just changed to default.`, {
          timeout: 5000,
          type: 'alert'
        })
      );
    }
  }
  return switchDevice;
}

export {
  setupAudioDeviceListeners,
  setupVideoDeviceListeners,
  setupPlaybackDeviceListener,
  handleDeviceChange,
  handleCameraDeviceChange,
  clearVideoDeviceListeners
};
